import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import {
  createInputRequestSchema,
  deleteInitiativeResponseSchema,
  duplicateInitiativeRequestSchema,
  initiativeWriteResponseSchema,
  transitionRequestSchema,
  transitionResponseSchema,
  type DeleteInitiativeResponse,
  type InitiativeFieldGroup,
  type InitiativeWriteResponse,
  type InputRequestDto,
  type TransitionResponse,
} from '@complan/contracts';
import type { InitiativeStatus } from '@complan/domain';
import { apiFetch } from '@/shared/api/client';
import { useInvalidateCalendar } from '@/features/calendar/use-calendar-freshness';
import { useInvalidateManpowerViews } from '@/features/manpower/use-invalidate-manpower-views';
import { detailQueryKeys } from './detail-data';

/**
 * The mutation wiring for the Detail_Panel footer actions (task 23.4's follow-up):
 * Request input, Cancel initiative, Delete and Duplicate. Edit reuses the intake
 * wizard's own `useUpdateInitiative` (`intake-data.ts`) from a dedicated route
 * rather than a mutation here.
 *
 * Each hook parses its payload through the `@complan/contracts` request schema
 * before sending, so a malformed call fails at the boundary rather than on the
 * wire, and parses the response through the matching response schema for the
 * same reason on the way back.
 */

/**
 * `POST /api/initiatives/:id/input-requests` (R2.6, R2.11). Text 1-2,000
 * characters; the server answers a length violation as `validation_failed`.
 * Invalidates the Input_Requests page so the new entry appears in the History
 * tab's list without a manual reload.
 */
export function useSubmitInputRequest(
  initiativeId: string,
): UseMutationResult<InputRequestDto, unknown, string> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (text: string): Promise<InputRequestDto> => {
      const body = createInputRequestSchema.parse({ text });
      const response = await apiFetch<unknown>(`/api/initiatives/${initiativeId}/input-requests`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      // The route answers the created row directly (id, initiativeId, actorUserId,
      // submittedAt, text) - the same shape the paginated list returns per row.
      return response as InputRequestDto;
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: detailQueryKeys.inputRequests(initiativeId) });
    },
  });
}

/**
 * `POST /api/initiatives/:id/transitions` with `targetStatus: 'cancelled'`
 * (R9.6): a required 1-500 character reason, then the status flips to Cancelled
 * with every other field, association, milestone and dependency retained
 * unchanged. Invalidates the detail projection (so the badge row and the
 * cancellation-reason row refresh) and the calendar (R13.3).
 */
export function useCancelInitiative(
  initiativeId: string,
  version: number,
): UseMutationResult<TransitionResponse, unknown, string> {
  const client = useQueryClient();
  const invalidateCalendar = useInvalidateCalendar();
  return useMutation({
    mutationFn: async (reason: string): Promise<TransitionResponse> => {
      const body = transitionRequestSchema.parse({
        targetStatus: 'cancelled',
        version,
        reason,
        comment: null,
      });
      const response = await apiFetch<unknown>(`/api/initiatives/${initiativeId}/transitions`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      return transitionResponseSchema.parse(response);
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: detailQueryKeys.detail(initiativeId) });
      void client.invalidateQueries({ queryKey: detailQueryKeys.history(initiativeId) });
      invalidateCalendar();
    },
  });
}

/** The payload a general status transition submits: the target and its optional/required reason and comment. */
export interface TransitionInput {
  readonly targetStatus: InitiativeStatus;
  /**
   * Required by the transition table for every transition except Draft → Submitted
   * (R9.13); `null` for that one. The dialog collects it when the target requires
   * it, and the server is the final authority on whether it was needed.
   */
  readonly reason: string | null;
  /** Always-optional free-text comment, up to 1,000 characters (R9.2). */
  readonly comment: string | null;
}

/**
 * `POST /api/initiatives/:id/transitions` for any lifecycle move other than
 * cancellation (R9.2, R9.3, R9.12, R9.13): Submit, return to Draft, move to
 * Planned, Approve, move back to Planned, or make Final. Cancellation has its own
 * hook ({@link useCancelInitiative}) because its dialog and copy differ; both
 * post to the same endpoint with the same schema.
 *
 * The `version` is the one the Detail_Panel loaded, so a stale view is answered
 * with a `409` conflict rather than transitioning a status the user did not
 * actually see (R5.8). Approver-gated targets (Approve, Final, and moves out of
 * Approved/Final) are refused server-side for a principal without approver rights
 * (R9.3); the footer only offers a control the principal's rights permit, so the
 * refusal is a backstop, not the primary gate.
 *
 * Invalidates the detail projection (so the status badge row refreshes),
 * the change history (the transition is a new history row, R9.2), the calendar
 * (R13.3), and every Initiative list page (the status column and its filter).
 */
export function useTransitionInitiative(
  initiativeId: string,
  version: number,
): UseMutationResult<TransitionResponse, unknown, TransitionInput> {
  const client = useQueryClient();
  const invalidateCalendar = useInvalidateCalendar();
  return useMutation({
    mutationFn: async (input: TransitionInput): Promise<TransitionResponse> => {
      const body = transitionRequestSchema.parse({
        targetStatus: input.targetStatus,
        version,
        reason: input.reason,
        comment: input.comment,
      });
      const response = await apiFetch<unknown>(`/api/initiatives/${initiativeId}/transitions`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      return transitionResponseSchema.parse(response);
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: detailQueryKeys.detail(initiativeId) });
      void client.invalidateQueries({ queryKey: detailQueryKeys.history(initiativeId) });
      // The list is keyed by its full view state, so invalidate by the shared
      // prefix rather than one page's key: a transition changes the status column
      // and can move the row in or out of a status-filtered view.
      void client.invalidateQueries({ queryKey: ['initiative-list'] });
      invalidateCalendar();
    },
  });
}

/**
 * `DELETE /api/initiatives/:id` (R9.9), version-guarded via the query string.
 * The record is soft-deleted; the caller closes the panel and invalidates the
 * calendar on success, since the record disappears from every readable view.
 *
 * Invalidates manpower views (task 14.4) since deleting an Initiative removes
 * its demand drivers from all calculations.
 */
export function useDeleteInitiative(
  initiativeId: string,
  version: number,
): UseMutationResult<DeleteInitiativeResponse, unknown, void> {
  const invalidateCalendar = useInvalidateCalendar();
  const invalidateManpower = useInvalidateManpowerViews();
  return useMutation({
    mutationFn: async (): Promise<DeleteInitiativeResponse> => {
      const response = await apiFetch<unknown>(
        `/api/initiatives/${initiativeId}?version=${String(version)}`,
        { method: 'DELETE' },
      );
      return deleteInitiativeResponseSchema.parse(response);
    },
    onSuccess: () => {
      invalidateCalendar();
      void invalidateManpower();
    },
  });
}

/**
 * `POST /api/initiatives/:id/duplicate` (R9.8): a new record with the selected
 * field groups copied, status Draft, empty approval history, cleared
 * changed-after-approval flag and the source's confidentiality attribute. The
 * caller navigates to the new record on success; the calendar is invalidated so
 * the new Draft appears there too (R13.3).
 *
 * Invalidates manpower views (task 14.4) since duplicating an Initiative with
 * field force demand inputs creates new demand drivers.
 */
export function useDuplicateInitiative(
  initiativeId: string,
): UseMutationResult<InitiativeWriteResponse, unknown, readonly InitiativeFieldGroup[]> {
  const invalidateCalendar = useInvalidateCalendar();
  const invalidateManpower = useInvalidateManpowerViews();
  return useMutation({
    mutationFn: async (
      fieldGroups: readonly InitiativeFieldGroup[],
    ): Promise<InitiativeWriteResponse> => {
      const body = duplicateInitiativeRequestSchema.parse({ fieldGroups });
      const response = await apiFetch<unknown>(`/api/initiatives/${initiativeId}/duplicate`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      return initiativeWriteResponseSchema.parse(response);
    },
    onSuccess: () => {
      invalidateCalendar();
      void invalidateManpower();
    },
  });
}
